import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Collapse, Theme, Typography } from '@mui/material';
import Check from '@mui/icons-material/Check';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { FullScreenTrackingContext } from '../../../Rooms/Tracking/FullScreenTrackingContext';
import { pluralizeLabel } from '../../../../lib/Language';
import { ReactComponent as SignableIcon } from '../../../../svg/icons/signable.svg';
import {
  SignableSchema,
  SignablesSchema,
} from '../../../../definitions/schemas';
import { SignableStatus } from '../../../../enums/Signables';
import { useI18n } from '../../../../lib/hook/useI18n';
import Pill from '../../../../theme/components/Pill/Pill';
import Signable, { PickedSignablesProps } from '../../../Signables/Signable';
import useContentScroll from '../../Layout/useContentScroll';

const useStyles = makeStyles((theme: Theme) => ({
  signableIcon: {
    color: theme.palette.secondary.main,
    height: theme.spacing(2.5),
    verticalAlign: 'top',
    width: theme.spacing(2.5),
  },
  stickyContainer: {
    position: 'sticky',
  },
}));

export interface RoomSignablesWrapperProps extends PickedSignablesProps {
  disableStickyPill?: boolean;
  onSignableSigned?: (signable: SignableSchema) => void;
  referrer: string;
  roomUuid: string | undefined;
  signables: SignablesSchema;
  userContactId?: string;
}

const RoomSignablesWrapper: React.FC<RoomSignablesWrapperProps> = (
  props: RoomSignablesWrapperProps
): JSX.Element => {
  const { setFullScreenContentId } = useContext(FullScreenTrackingContext);
  const classes = useStyles();
  const { t } = useI18n();

  const {
    disableChangesOnScroll,
    disableSign,
    disableStickyPill = false,
    downloadSignableId,
    onSignableSigned,
    referrer,
    roomUuid,
    scrollThresholdBase = 0,
    signables,
    userContactId,
  } = props;

  const [expandCompleted, setExpandCompleted] = useState(false);
  /**
   * Utility flag to force a re-render for the scroll trigger to work properly
   */
  const [, setMounted] = useState(false);

  /**
   * Categorizes the room's signables into completed and incomplete, also
   * an additional array is required to determine whether or not there are
   * signables that require a signature (still pending). The first two
   * categories are used for render purposes, the last one is just to
   * display the sticky pill that allows users to quickly scroll up to
   * see the signable docs.
   */
  const [completedSignables, incompleteSignables, pendingSignables] =
    useMemo(() => {
      const completed: SignablesSchema = [];
      const incomplete: SignablesSchema = [];
      const pending: SignablesSchema = [];

      signables.forEach((signable) => {
        const isCompleted = signable.status === SignableStatus.COMPLETED;
        if (isCompleted) {
          completed.push(signable);
          return;
        }

        incomplete.push(signable);
        if (signable.pendingSignature) {
          pending.push(signable);
        }
      });

      return [completed, incomplete, pending];
    }, [signables]);

  /**
   * Ref to an empty element that's placed on top of this wrapper that helps
   * the user to reset the scroll in order to view all other signable documents
   */
  const scrollReset = useRef<HTMLDivElement>(null);

  const isContentScrolling = useContentScroll(scrollThresholdBase);

  /**
   * Forcing a component re-render to allow the scroll trigger to properly
   * query and attach the scroll event listeners. This is only for the
   * room contact view, as there's no way to get a direct access via ref
   * to the scroll target
   */
  useEffect(() => {
    setMounted(true);
  }, []);

  const toggleCompletedSignables = () => {
    setExpandCompleted((previousValue) => !previousValue);
  };

  /**
   * Sets an id to the full screen context in order to stop any contents
   * from tracking whilst the signable document is being signed, since
   * they're not actually being viewed by the user
   */
  const onInhouseSignerToggled = (open: boolean) => {
    const signableId = signables[0]?.id;
    if (signableId) {
      setFullScreenContentId(open ? `signable-document-${signableId}` : null);
    }
  };

  const onResetScroll = () => {
    scrollReset.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const renderStickyContainer = (
    sticky: boolean,
    children: React.ReactNode,
    mb = 0,
    top = 2
  ): JSX.Element => (
    <Box
      className={classNames({
        [classes.stickyContainer]: sticky,
      })}
      sx={(theme) => ({
        mb,
        top: theme.spacing(top),
        zIndex: 3,
      })}
    >
      {children}
    </Box>
  );

  const renderSignable = (signable: SignableSchema): JSX.Element => (
    <Signable
      key={`signable-${signable.id}`}
      disableChangesOnScroll={disableChangesOnScroll}
      disableSign={disableSign}
      downloadSignableId={downloadSignableId}
      onInhouseSignerToggled={onInhouseSignerToggled}
      onSynced={onSignableSigned}
      referrer={referrer}
      roomUuid={roomUuid}
      scrollThresholdBase={scrollThresholdBase}
      signable={signable}
      userContactId={userContactId}
    />
  );

  const renderPendingSignablesPill = (): JSX.Element | undefined => {
    const amount = pendingSignables.length;
    if (disableStickyPill || amount === 0) {
      return undefined;
    }

    return renderStickyContainer(
      true,
      <Box display="flex" justifyContent="center" mb={-6} pt={1}>
        <Pill
          bold
          hasElevation={isContentScrolling}
          onClick={onResetScroll}
          startIcon={
            <Box sx={(theme) => ({ height: theme.spacing(2.5) })}>
              <SignableIcon className={classes.signableIcon} />
            </Box>
          }
          title={t('esignature.sign.pending_amount', {
            amount,
            pluralLabel: pluralizeLabel(amount, 'esignature.sign.document'),
          })}
          widthAuto
        />
      </Box>,
      0,
      1
    );
  };

  /**
   * Usually the signables to render here are only the incomplete/pending signables,
   * but if there are no more than 2 completed signables, those won't be collapsed,
   * so we need to render them here as well
   */
  const renderSignables = (): JSX.Element | undefined => {
    let signablesToRender = incompleteSignables;
    if (completedSignables.length < 2) {
      signablesToRender = signables;
    }

    return (
      <>
        {renderPendingSignablesPill()}
        <Box
          sx={(theme) => ({
            background: theme.palette.background.paper,
            marginBottom: 6,
            position: 'relative',
            zIndex: 3,
          })}
        >
          {signablesToRender.map((signable) => (
            <Box key={`signable-container-${signable.id}`} mb={1}>
              {renderSignable(signable)}
            </Box>
          ))}
        </Box>
      </>
    );
  };

  /**
   * Completed signables are collapsed only if there are 2 or more
   */
  const renderCompletedSignables = (): JSX.Element | undefined => {
    const amount = completedSignables.length;
    if (amount < 2) {
      return undefined;
    }

    const Icon = expandCompleted ? ExpandLess : ExpandMore;
    const pluralLabel = pluralizeLabel(amount, 'esignature.sign.document');

    return (
      <>
        <Box
          onClick={toggleCompletedSignables}
          sx={(theme) => ({
            backgroundColor: 'var(--success-color-background)',
            borderRadius: theme.spacing(1),
            cursor: 'pointer',
            marginBottom: 1,
            padding: theme.spacing(2),
          })}
        >
          <Box alignItems="center" display="flex">
            <Check color="success" fontSize="small" />
            <Box flex="1" ml={1.5}>
              <Typography sx={{ color: 'success.main' }} variant="body2">
                <Box component="strong">
                  {t('esignature.sign.completed_amount', {
                    amount,
                    pluralLabel: pluralLabel.toLowerCase(),
                  })}
                </Box>
              </Typography>
            </Box>
            <Icon color="success" fontSize="small" />
          </Box>
        </Box>

        <Collapse in={expandCompleted}>
          {completedSignables.map((signable) => (
            <Box key={`signable-container-${signable.id}`} mb={1}>
              {renderSignable(signable)}
            </Box>
          ))}
        </Collapse>
      </>
    );
  };

  const renderContent = (): JSX.Element | undefined => {
    if (signables.length > 1) {
      return renderSignables();
    }

    const signable = signables[0];
    return renderStickyContainer(
      signable.pendingSignature,
      renderSignable(signable),
      4
    );
  };

  return (
    <>
      <Box ref={scrollReset} />
      {renderCompletedSignables()}
      {renderContent()}
    </>
  );
};

export default RoomSignablesWrapper;
